import React, { useEffect } from 'react';
import { Details } from 'nhsuk-react-components';
import { Link } from 'react-router-dom';
import { IterationRule, RuleType } from 'schemas/IterationRule.schema.ts';
import { reduceRulesWithAndClauses } from 'utils/ruleUtils.ts';
import useCampaignNavigate from 'hooks/useCampaignNavigate.ts';
import useUserAuthorisation from 'hooks/useUserAuthorisation.ts';
import { RulesContent } from 'schemas/Content.schema.ts';
import { getFilterRulesContent, getRoutingRulesContent, getSuppressionRulesContent } from 'utils/contentUtils.ts';
import { RoutingPlans } from 'schemas/RoutingPlan.schema.ts';
import { CampaignTemplate } from 'schemas/CampaignTemplates.schema.ts';
import RuleSection from './RulesSection.tsx';
import './RulesTable.scss';

interface AddRuleButtonProps {
    iterationId: string;
    ruleType: string;
    ruleName: string;
}
function AddRuleButton({
  iterationId,
  ruleType,
  ruleName,
}: AddRuleButtonProps) {
  const { getCampaignPathFor } = useCampaignNavigate();

  return (
    <center>
      <Link
        to={getCampaignPathFor(`iteration/${iterationId}/${ruleType}-rules/new/library`)}
        data-testid={`add-${ruleType}-rule`}
        aria-label={`Add a ${ruleName}`}
      >
        Add a rule
      </Link>
    </center>
  );
}

interface RulesTableProps {
  rules: IterationRule[];
  teamDefaultRules?: IterationRule[];
  iterationId: string;
  canUpdateFilterRules: boolean;
  canUpdateSuppressionRules: boolean;
  canUpdateRoutingRules: boolean;
  handleRulesSave: (rules: IterationRule[], ruleType: RuleType) => void;
  openRules: string;
  rulesContent: RulesContent[];
  teamRoutingPlans?: RoutingPlans;
  campaignTemplate?: CampaignTemplate;
}

export default function RulesTable({
  rules,
  teamDefaultRules = [],
  iterationId,
  canUpdateFilterRules,
  canUpdateSuppressionRules,
  canUpdateRoutingRules,
  handleRulesSave,
  openRules,
  rulesContent,
  teamRoutingPlans,
  campaignTemplate,
}: RulesTableProps) {
  const MANDATORY_RULES_MESSAGE = 'Mandatory rules apply to every campaign. They are shown in grey because they cannot be edited or removed.';

  const { canCreateCustomRules } = useUserAuthorisation();
  const rulesWithAndClauses = rules.reduce(reduceRulesWithAndClauses, []);

  const filterRules = rulesWithAndClauses.filter((rule) => rule.Type === 'F');
  const suppressionRules = rulesWithAndClauses.filter(
    (rule) => rule.Type === 'S',
  );
  const routingRules = rulesWithAndClauses.filter(
    (rule) => rule.Type === 'R',
  );

  const teamDefaultRulesWithAndClauses = teamDefaultRules.reduce(reduceRulesWithAndClauses, []);

  const filterDefaultRules = teamDefaultRulesWithAndClauses.filter((rule) => rule.Type === 'F');
  const suppressionDefaultRules = teamDefaultRulesWithAndClauses.filter(
    (rule) => rule.Type === 'S',
  );
  const routingDefaultRules = teamDefaultRulesWithAndClauses.filter(
    (rule) => rule.Type === 'R',
  );

  // Ref for Filter rules section
  const filterDetailsRef = React.useRef<HTMLDivElement>(null);

  const filterRulesContent = getFilterRulesContent(rulesContent);
  const suppressionRulesContent = getSuppressionRulesContent(rulesContent);
  const routingRulesContent = getRoutingRulesContent(rulesContent);

  useEffect(() => {
    if (openRules === 'F' && filterDetailsRef.current) {
      filterDetailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [openRules]);

  return (
    <>
      {
        campaignTemplate?.['IterationDetails.IterationRules.filters.input.display'] === 'hidden'
          && filterRules.length + filterDefaultRules.length === 0
          ? null
          : (
            <Details className="rule-details" open={openRules === 'F'}>
              <Details.Summary>
                Filter rules (
                {filterRules.length + filterDefaultRules.length}
                )
              </Details.Summary>
              <Details.Text>
                <div className="nhsuk-u-padding-top-4 nhsuk-u-padding-bottom-4">
                  {filterRulesContent?.description}
                </div>
                {filterDefaultRules.length > 0 && (
                <p>{MANDATORY_RULES_MESSAGE}</p>
                )}
                <RuleSection
                  ruleType="F"
                  ruleLabel="Filter rule"
                  rules={filterRules}
                  defaultRules={filterDefaultRules}
                  iterationId={iterationId}
                  canUpdate={canUpdateFilterRules}
                  canCreateCustomRules={canCreateCustomRules}
                  handleRulesSave={handleRulesSave}
                  teamRoutingPlans={teamRoutingPlans}
                />
                {canUpdateFilterRules && (
                <AddRuleButton
                  iterationId={iterationId}
                  ruleType="filter"
                  ruleName="filter rule"
                />
                )}
              </Details.Text>
            </Details>
          )
      }

      {
        campaignTemplate?.['IterationDetails.IterationRules.suppressions.input.display'] === 'hidden'
          && suppressionRules.length + suppressionDefaultRules.length === 0
          ? null
          : (
            <Details className="rule-details" open={openRules === 'S'}>
              <Details.Summary>
                Suppression rules (
                {suppressionRules.length + suppressionDefaultRules.length}
                )
              </Details.Summary>
              <Details.Text>
                <div className="nhsuk-u-padding-top-4 nhsuk-u-padding-bottom-4">
                  {suppressionRulesContent?.description}
                </div>
                {suppressionDefaultRules.length > 0 && (
                <p>{MANDATORY_RULES_MESSAGE}</p>
                )}
                <RuleSection
                  ruleType="S"
                  ruleLabel="Suppression rule"
                  rules={suppressionRules}
                  defaultRules={suppressionDefaultRules}
                  iterationId={iterationId}
                  canUpdate={canUpdateSuppressionRules}
                  canCreateCustomRules={canCreateCustomRules}
                  handleRulesSave={handleRulesSave}
                  teamRoutingPlans={teamRoutingPlans}
                />
                {canUpdateSuppressionRules && (
                <AddRuleButton
                  iterationId={iterationId}
                  ruleType="suppression"
                  ruleName="suppression rule"
                />
                )}
              </Details.Text>
            </Details>
          )
      }
      {
        campaignTemplate?.['IterationDetails.IterationRules.routing.input.display'] === 'hidden'
          && routingRules.length + routingDefaultRules.length === 0
          ? null
          : (
            <Details className="rule-details" open={openRules === 'R'}>
              <Details.Summary>
                Routing rules (
                {routingRules.length + routingDefaultRules.length}
                )
              </Details.Summary>
              <Details.Text>
                <div className="nhsuk-u-padding-top-4 nhsuk-u-padding-bottom-4">
                  {routingRulesContent?.description}
                </div>
                {routingDefaultRules.length > 0 && (
                <p>{MANDATORY_RULES_MESSAGE}</p>
                )}
                <RuleSection
                  ruleType="R"
                  ruleLabel="Routing rule"
                  rules={routingRules}
                  defaultRules={routingDefaultRules}
                  iterationId={iterationId}
                  canUpdate={canUpdateRoutingRules}
                  canCreateCustomRules={canCreateCustomRules}
                  handleRulesSave={handleRulesSave}
                  teamRoutingPlans={teamRoutingPlans}
                />
                {canUpdateRoutingRules && (
                <AddRuleButton
                  iterationId={iterationId}
                  ruleType="routing"
                  ruleName="routing rule"
                />
                )}
              </Details.Text>
            </Details>
          )
      }
    </>
  );
}
